// admin/src/pages/ScimIntegrations/OutboundTargetDrawer.js

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  Chip,
  Button,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KeyIcon from '@mui/icons-material/Key';
import SyncIcon from '@mui/icons-material/Sync';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  getOutboundTarget,
  getOutboundTargetStatus,
  updateOutboundTargetStatus,
  rotateOutboundTargetToken,
  deleteOutboundTarget,
  testOutboundTarget,
  syncOutboundTarget,
  listOutboundTargetMappings,
  retryOutboundTargetMapping,
  listOutboundTargetActivity,
  extractOutboundErrorMessage,
} from './scimOutboundApi';
import { getStatusChipSx } from './scimStatusStyle';

import OutboundTargetDialog from './OutboundTargetDialog';

export default function OutboundTargetDrawer({
  open,
  targetId,
  onClose,
  canCreate,
  canUpdate,
  canDelete,
  onTargetDeleted,
  onTargetUpdated,
}) {
  const [target, setTarget] = useState(null);
  const [targetStatus, setTargetStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);

  // Rotate token dialog state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateTokenValue, setRotateTokenValue] = useState('');
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateError, setRotateError] = useState(null);

  // Delete typed confirmation dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Sync confirmation & execution state
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Test connection state
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Toggle status busy
  const [statusBusy, setStatusBusy] = useState(false);

  // Mappings table state
  const [mappings, setMappings] = useState([]);
  const [mappingStateFilter, setMappingStateFilter] = useState('all');
  const [mappingPage, setMappingPage] = useState(0);
  const [mappingLimit, setMappingLimit] = useState(10);
  const [mappingTotal, setMappingTotal] = useState(0);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [retryingMappingId, setRetryingMappingId] = useState(null);

  // Activity table state
  const [activities, setActivities] = useState([]);
  const [activityOperationFilter, setActivityOperationFilter] = useState('all');
  const [activityOutcomeFilter, setActivityOutcomeFilter] = useState('all');
  const [activityPage, setActivityPage] = useState(0);
  const [activityLimit, setActivityLimit] = useState(10);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);

  // Load target details & status
  const loadTargetData = async () => {
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      const [tgtRes, statusRes] = await Promise.all([
        getOutboundTarget(targetId),
        getOutboundTargetStatus(targetId).catch(() => null),
      ]);
      setTarget(tgtRes?.data || null);
      setTargetStatus(statusRes?.data || null);
    } catch (err) {
      setError(extractOutboundErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Load mappings
  const loadMappings = async () => {
    if (!targetId) return;
    setMappingLoading(true);
    try {
      const res = await listOutboundTargetMappings(targetId, {
        page: mappingPage + 1,
        limit: mappingLimit,
        state: mappingStateFilter,
      });
      setMappings(res?.data || []);
      setMappingTotal(res?.pagination?.total || (res?.data || []).length);
    } catch (err) {
      setMappings([]);
    } finally {
      setMappingLoading(false);
    }
  };

  // Load activity
  const loadActivity = async () => {
    if (!targetId) return;
    setActivityLoading(true);
    try {
      const res = await listOutboundTargetActivity(targetId, {
        page: activityPage + 1,
        limit: activityLimit,
        operation: activityOperationFilter,
        outcome: activityOutcomeFilter,
      });
      setActivities(res?.data || []);
      setActivityTotal(res?.pagination?.total || (res?.data || []).length);
    } catch (err) {
      setActivities([]);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (open && targetId) {
      loadTargetData();
    } else {
      setTarget(null);
      setTargetStatus(null);
      setError(null);
      setActionMessage(null);
      setTestResult(null);
      setSyncResult(null);
    }
  }, [open, targetId]);

  useEffect(() => {
    if (open && targetId) {
      loadMappings();
    }
  }, [open, targetId, mappingStateFilter, mappingPage, mappingLimit]);

  useEffect(() => {
    if (open && targetId) {
      loadActivity();
    }
  }, [open, targetId, activityOperationFilter, activityOutcomeFilter, activityPage, activityLimit]);

  // Test Connection Handler
  const handleTestConnection = async () => {
    if (!canUpdate || !targetId) return;
    setTestBusy(true);
    setTestResult(null);
    setActionMessage(null);
    try {
      const res = await testOutboundTarget(targetId);
      if (res?.data?.reachable) {
        setTestResult({
          success: true,
          capabilities: res.data.capabilities || {},
        });
      } else {
        // Fixed error message only: never raw server text or data.error
        setTestResult({
          success: false,
          message: 'Target unreachable or blocked by security policy.',
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: 'Target unreachable or blocked by security policy.',
      });
    } finally {
      setTestBusy(false);
    }
  };

  // Sync Now Handler
  const handleSyncNow = async () => {
    if (!canUpdate || !targetId) return;
    setSyncBusy(true);
    setSyncResult(null);
    setActionMessage(null);
    try {
      const res = await syncOutboundTarget(targetId);
      setSyncResult({
        success: true,
        stats: res?.data || {},
      });
      setSyncConfirmOpen(false);
      // Refresh status and telemetry
      loadTargetData();
      loadMappings();
      loadActivity();
    } catch (err) {
      // 409 mapped strictly to fixed message
      const msg = extractOutboundErrorMessage(err);
      setSyncResult({
        success: false,
        message: msg,
      });
    } finally {
      setSyncBusy(false);
    }
  };

  // Toggle Status Handler
  const handleToggleStatus = async () => {
    if (!canUpdate || !target) return;
    const nextStatus = target.status === 'active' ? 'disabled' : 'active';
    setStatusBusy(true);
    setActionMessage(null);
    try {
      await updateOutboundTargetStatus(target._id, nextStatus);
      setTarget((prev) => ({ ...prev, status: nextStatus }));
      setActionMessage({
        type: 'success',
        text: `Target ${nextStatus === 'active' ? 'enabled' : 'disabled'} successfully.`,
      });
      if (onTargetUpdated) onTargetUpdated();
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: extractOutboundErrorMessage(err),
      });
    } finally {
      setStatusBusy(false);
    }
  };

  // Rotate Token Handler
  const handleRotateToken = async () => {
    if (!canUpdate || !target) return;
    if (!rotateTokenValue.trim()) {
      setRotateError('New bearer token is required.');
      return;
    }
    setRotateBusy(true);
    setRotateError(null);
    try {
      const res = await rotateOutboundTargetToken(target._id, rotateTokenValue.trim());
      const newPrefix = res?.data?.tokenPrefix || '****';
      setTarget((prev) => ({ ...prev, tokenPrefix: newPrefix }));
      // Token cleared immediately after submit, never logged
      setRotateTokenValue('');
      setRotateOpen(false);
      setActionMessage({
        type: 'success',
        text: `Bearer token rotated successfully (Prefix: ${newPrefix}).`,
      });
      if (onTargetUpdated) onTargetUpdated();
    } catch (err) {
      setRotateError(extractOutboundErrorMessage(err));
    } finally {
      setRotateBusy(false);
      setRotateTokenValue('');
    }
  };

  // Retry Mapping Handler
  const handleRetryMapping = async (mappingId) => {
    if (!canUpdate || !targetId) return;
    setRetryingMappingId(mappingId);
    try {
      await retryOutboundTargetMapping(targetId, mappingId);
      await loadMappings();
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: extractOutboundErrorMessage(err),
      });
    } finally {
      setRetryingMappingId(null);
    }
  };

  // Delete Target Handler
  const handleDeleteTarget = async () => {
    if (!canDelete || !target) return;
    if (typedName !== target.name) {
      setDeleteError('Target name does not match.');
      return;
    }
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteOutboundTarget(target._id);
      setDeleteOpen(false);
      onClose();
      if (onTargetDeleted) onTargetDeleted();
    } catch (err) {
      setDeleteError(extractOutboundErrorMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        transitionDuration={0}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 800 },
            p: 3,
            boxSizing: 'border-box',
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 700 }} id="outbound-drawer-title">
              {target?.name || 'Target Details'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {target?._id}
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small" id="close-outbound-drawer-btn">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : target ? (
          <Stack spacing={3}>
            {actionMessage && (
              <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)}>
                {actionMessage.text}
              </Alert>
            )}

            {/* Target Overview Card */}
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Base URL
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {target.baseUrl}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={target.status?.toUpperCase()}
                        sx={{ ...getStatusChipSx(target.status), fontWeight: 700 }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Mode
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {target.dryRun ? (
                        <Chip
                          size="small"
                          label="DRY RUN"
                          color="warning"
                          variant="filled"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="LIVE"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Compat Mode
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {target.compatMode}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Scope Type
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {target.scope?.type === 'all'
                        ? 'All users'
                        : target.scope?.type === 'group'
                          ? `Group (${target.scope?.groupIds?.length || 0} groups)`
                          : `Users (${target.scope?.userIds?.length || 0} users)`}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Token Prefix
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {target.tokenPrefix || '****'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Rate Limit
                    </Typography>
                    <Typography variant="body2">
                      {target.rateLimitPerMin || 120} req/min
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Action Bar */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {canUpdate && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testBusy ? <CircularProgress size={16} /> : <NetworkCheckIcon />}
                  onClick={handleTestConnection}
                  disabled={testBusy}
                  id="test-outbound-target-btn"
                >
                  Test Connection
                </Button>
              )}

              {canUpdate && (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={<SyncIcon />}
                  onClick={() => {
                    setSyncResult(null);
                    setSyncConfirmOpen(true);
                  }}
                  id="sync-outbound-target-btn"
                >
                  Sync Now
                </Button>
              )}

              {canUpdate && (
                <Button
                  variant="outlined"
                  size="small"
                  color={target.status === 'active' ? 'warning' : 'success'}
                  startIcon={statusBusy ? <CircularProgress size={16} /> : target.status === 'active' ? <BlockIcon /> : <CheckCircleOutlineIcon />}
                  onClick={handleToggleStatus}
                  disabled={statusBusy}
                  id="toggle-outbound-target-status-btn"
                >
                  {target.status === 'active' ? 'Disable Target' : 'Enable Target'}
                </Button>
              )}

              {canUpdate && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<KeyIcon />}
                  onClick={() => {
                    setRotateTokenValue('');
                    setRotateError(null);
                    setRotateOpen(true);
                  }}
                  id="rotate-outbound-token-btn"
                >
                  Rotate Token
                </Button>
              )}

              {canUpdate && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOpen(true)}
                  id="edit-outbound-target-btn"
                >
                  Edit
                </Button>
              )}

              {canDelete && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => {
                    setTypedName('');
                    setDeleteError(null);
                    setDeleteOpen(true);
                  }}
                  id="delete-outbound-target-btn"
                >
                  Delete Target
                </Button>
              )}
            </Stack>

            {/* Test Connection Results Alert */}
            {testResult && (
              <Box>
                {testResult.success ? (
                  <Alert severity="success" id="test-connection-success-alert">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Connection Successful
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Remote endpoint is reachable and SCIM ServiceProviderConfig responded.
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`PATCH: ${testResult.capabilities.patch ? 'Supported' : 'No'}`} />
                      <Chip size="small" label={`Bulk: ${testResult.capabilities.bulk ? 'Supported' : 'No'}`} />
                      <Chip size="small" label={`Filter: ${testResult.capabilities.filter ? 'Supported' : 'No'}`} />
                      <Chip size="small" label={`Password Change: ${testResult.capabilities.changePassword ? 'Supported' : 'No'}`} />
                      <Chip size="small" label={`ETag: ${testResult.capabilities.etag ? 'Supported' : 'No'}`} />
                    </Box>
                  </Alert>
                ) : (
                  <Alert severity="error" id="test-connection-fail-alert">
                    {testResult.message}
                  </Alert>
                )}
              </Box>
            )}

            {/* Sync Results Alert */}
            {syncResult && (
              <Box>
                {syncResult.success ? (
                  <Alert severity="success" id="sync-result-success-alert">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Sync Cycle Completed
                    </Typography>
                    <Typography variant="body2">
                      Created: {syncResult.stats.created || 0} | Updated: {syncResult.stats.updated || 0} | Deactivated: {syncResult.stats.deactivated || 0} | Deleted: {syncResult.stats.deleted || 0} | Failed: {syncResult.stats.failed || 0}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error" id="sync-result-error-alert">
                    {syncResult.message}
                  </Alert>
                )}
              </Box>
            )}

            {/* Telemetry & Metrics Windows */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Sync Telemetry & Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      24h Success / Fail
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {targetStatus?.metrics?.['24h']?.successCount || 0} /{' '}
                      <span style={{ color: targetStatus?.metrics?.['24h']?.failureCount ? '#d32f2f' : 'inherit' }}>
                        {targetStatus?.metrics?.['24h']?.failureCount || 0}
                      </span>
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      7d Success / Fail
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {targetStatus?.metrics?.['7d']?.successCount || 0} /{' '}
                      <span style={{ color: targetStatus?.metrics?.['7d']?.failureCount ? '#d32f2f' : 'inherit' }}>
                        {targetStatus?.metrics?.['7d']?.failureCount || 0}
                      </span>
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Sync
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {targetStatus?.lastSyncAt ? new Date(targetStatus.lastSyncAt).toLocaleString() : 'Never'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Error
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {targetStatus?.lastErrorCategory || 'None'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Provisioning Mappings Table */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  User Mappings ({mappingTotal})
                </Typography>
                <FormControl size="small" sx={{ width: 160 }}>
                  <InputLabel id="mapping-state-filter-label">State</InputLabel>
                  <Select
                    labelId="mapping-state-filter-label"
                    value={mappingStateFilter}
                    label="State"
                    onChange={(e) => {
                      setMappingStateFilter(e.target.value);
                      setMappingPage(0);
                    }}
                    id="mapping-state-filter-select"
                  >
                    <MenuItem value="all">All States</MenuItem>
                    <MenuItem value="synced">Synced</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="deactivated">Deactivated</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                    <MenuItem value="conflict">Conflict</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Remote ID</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Failures</TableCell>
                      <TableCell>Last Error</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappingLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : mappings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No user mappings found for this target.
                        </TableCell>
                      </TableRow>
                    ) : (
                      mappings.map((m) => (
                        <TableRow key={m._id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {m.userName || m.email || m.userId}
                            </Typography>
                            {m.email && m.userName && (
                              <Typography variant="caption" color="text.secondary">
                                {m.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {m.remoteId ? m.remoteId.slice(0, 12) + '...' : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={m.state}
                              sx={{ ...getStatusChipSx(m.state), fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell>{m.failureCount || 0}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {m.lastErrorCategory || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {m.state === 'failed' && canUpdate ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleRetryMapping(m._id)}
                                disabled={retryingMappingId === m._id}
                                id={`retry-mapping-${m._id}`}
                              >
                                {retryingMappingId === m._id ? 'Retrying...' : 'Retry'}
                              </Button>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={mappingTotal}
                  page={mappingPage}
                  onPageChange={(e, newPage) => setMappingPage(newPage)}
                  rowsPerPage={mappingLimit}
                  onRowsPerPageChange={(e) => {
                    setMappingLimit(parseInt(e.target.value, 10));
                    setMappingPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                />
              </TableContainer>
            </Box>

            {/* Outbound Activity Log */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Recent Activity ({activityTotal})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ width: 140 }}>
                    <InputLabel id="act-op-filter-label">Operation</InputLabel>
                    <Select
                      labelId="act-op-filter-label"
                      value={activityOperationFilter}
                      label="Operation"
                      onChange={(e) => {
                        setActivityOperationFilter(e.target.value);
                        setActivityPage(0);
                      }}
                      id="activity-operation-filter-select"
                    >
                      <MenuItem value="all">All Operations</MenuItem>
                      <MenuItem value="create">Create</MenuItem>
                      <MenuItem value="update">Update</MenuItem>
                      <MenuItem value="deactivate">Deactivate</MenuItem>
                      <MenuItem value="delete">Delete</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel id="act-out-filter-label">Outcome</InputLabel>
                    <Select
                      labelId="act-out-filter-label"
                      value={activityOutcomeFilter}
                      label="Outcome"
                      onChange={(e) => {
                        setActivityOutcomeFilter(e.target.value);
                        setActivityPage(0);
                      }}
                      id="activity-outcome-filter-select"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="failure">Failure</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Operation</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Status Code</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Attribute Names</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : activities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No activity recorded for this target yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      activities.map((act) => (
                        <TableRow key={act._id}>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {act.timestamp ? new Date(act.timestamp).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={act.operation}
                              variant="outlined"
                              sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={act.outcome}
                              color={act.outcome === 'success' ? 'success' : 'error'}
                              sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {act.statusCode || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {act.durationMs !== undefined ? `${act.durationMs}ms` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {/* Attribute Names ONLY - NEVER values or bodies */}
                            {Array.isArray(act.attributeNames) && act.attributeNames.length > 0 ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {act.attributeNames.map((name, i) => (
                                  <Chip key={i} size="small" label={name} sx={{ fontSize: '0.7rem' }} />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={activityTotal}
                  page={activityPage}
                  onPageChange={(e, newPage) => setActivityPage(newPage)}
                  rowsPerPage={activityLimit}
                  onRowsPerPageChange={(e) => {
                    setActivityLimit(parseInt(e.target.value, 10));
                    setActivityPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                />
              </TableContainer>
            </Box>
          </Stack>
        ) : null}
      </Drawer>

      {/* Sync Confirmation Dialog */}
      <Dialog
        open={syncConfirmOpen}
        transitionDuration={0}
        onClose={syncBusy ? undefined : () => setSyncConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Run Outbound Sync</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Trigger a synchronization cycle now for target <strong>{target?.name}</strong>?
            {target?.dryRun && (
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'warning.main', fontWeight: 600 }}>
                (Target is in DRY RUN mode. No remote changes will be committed.)
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmOpen(false)} disabled={syncBusy} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSyncNow}
            variant="contained"
            color="primary"
            disabled={syncBusy}
            id="confirm-sync-btn"
            startIcon={syncBusy ? <CircularProgress size={16} /> : null}
          >
            {syncBusy ? 'Syncing...' : 'Start Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate Token Dialog */}
      <Dialog
        open={rotateOpen}
        onClose={rotateBusy ? undefined : () => setRotateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rotate Bearer Token</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Provide the new bearer token for <strong>{target?.name}</strong>.
            This token is write-only, will not be displayed, and is immediately cleared from memory.
          </DialogContentText>
          {rotateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rotateError}
            </Alert>
          )}
          <TextField
            label="New Bearer Token"
            type="password"
            autoComplete="off"
            value={rotateTokenValue}
            onChange={(e) => setRotateTokenValue(e.target.value)}
            disabled={rotateBusy}
            fullWidth
            required
            id="rotate-token-input"
            helperText="Bearer token provided by destination SCIM server"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateOpen(false)} disabled={rotateBusy} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRotateToken}
            variant="contained"
            color="primary"
            disabled={rotateBusy || !rotateTokenValue.trim()}
            id="confirm-rotate-token-btn"
            startIcon={rotateBusy ? <CircularProgress size={16} /> : null}
          >
            {rotateBusy ? 'Rotating...' : 'Rotate Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Target Dialog */}
      {editOpen && (
        <OutboundTargetDialog
          open={editOpen}
          target={target}
          onClose={() => setEditOpen(false)}
          onSuccess={() => {
            loadTargetData();
            if (onTargetUpdated) onTargetUpdated();
          }}
        />
      )}

      {/* Delete Target Dialog */}
      <Dialog
        open={deleteOpen}
        transitionDuration={0}
        onClose={deleteBusy ? undefined : () => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Delete Outbound Target</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This action is permanent and will remove target <strong>{target?.name}</strong> and all associated user mappings and telemetry records.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm deletion, please type <strong>{target?.name}</strong> below:
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <TextField
            label="Target Name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            disabled={deleteBusy}
            fullWidth
            id="delete-target-confirm-input"
            inputProps={{ id: 'delete-target-confirm-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteBusy} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTarget}
            variant="contained"
            color="error"
            disabled={deleteBusy || typedName !== target?.name}
            id="confirm-delete-target-btn"
            startIcon={deleteBusy ? <CircularProgress size={16} /> : null}
          >
            {deleteBusy ? 'Deleting...' : 'Delete Target'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
